import { useEffect, useRef, useState } from "react";
import { ViewerHost } from "../ViewerHost";
import type { ViewerClient } from "../../client/ViewerClient";
import type { CameraPreset, Catalogs } from "../../shared/api-contract";
import { getModelGlbUrl, getModel } from "../../features/vault/vaultApi";
import { updateJob, addJobOutput } from "../../features/render/renderApi";
import { capsFor } from "../../ui/editor/caps";
import { withEmbeddedScene } from "../../util/embeddedScene";
import { getEffectiveSettings } from "../../features/rendersettings/renderSettingsApi";

// Camera presets in the same order the settings UI offers 1/4/8 angles.
const ANGLE_PRESETS: CameraPreset[] = ["threeQuarter", "front", "left", "top", "right", "back"];

/** Resolution label → pixels, matching the render-settings UI's own options. */
const RESOLUTION_PX: Record<string, [number, number]> = {
  "1080p": [1920, 1080], "2K": [2560, 1440], "4K": [3840, 2160], "8K": [7680, 4320],
};

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

interface RunnerJob {
  _id: string;
  fileId: string;
}

/**
 * In-browser Bulk Render: renders each job's model in ONE hidden offscreen
 * viewer (same pattern as PosterWorker) — load → per metal, per angle snapshot
 * → POST the PNG to the batch job → mark done → remove. Processes jobs
 * sequentially. Mount it while a browser/cloud batch is running; unmount when
 * the batch reaches a terminal state and the viewer disposes with it.
 */
export default function BrowserRenderRunner({
  jobs,
  angles,
  metals,
  width,
  height,
  video,
  useEffective,
  onProgress,
  onDone,
}: {
  jobs: RunnerJob[];
  angles: number;
  metals: number;
  width: number;
  height: number;
  /** Also record a turntable video per model (in addition to the stills). */
  video?: boolean;
  /** Honour each file's own resolution/background from the render-settings tree. */
  useEffective?: boolean;
  onProgress?: () => void;
  onDone?: () => void;
}) {
  if (jobs.length === 0) return null;
  return (
    <div aria-hidden style={{ position: "fixed", left: -10000, top: 0, width: 512, height: 512, pointerEvents: "none" }}>
      <ViewerHost mode="ring">
        {({ ready, client, iframe }) => (
          <Runner
            ready={ready}
            client={client}
            iframe={iframe}
            jobs={jobs}
            angles={angles}
            metals={metals}
            width={width}
            height={height}
            video={video}
            useEffective={useEffective}
            onProgress={onProgress}
            onDone={onDone}
          />
        )}
      </ViewerHost>
    </div>
  );
}

function Runner({
  ready,
  client,
  iframe,
  jobs,
  angles,
  metals,
  width,
  height,
  video,
  useEffective,
  onProgress,
  onDone,
}: {
  ready: boolean;
  client: ViewerClient | null;
  iframe: React.ReactNode;
  jobs: RunnerJob[];
  angles: number;
  metals: number;
  width: number;
  height: number;
  video?: boolean;
  useEffective?: boolean;
  onProgress?: () => void;
  onDone?: () => void;
}) {
  const done = useRef<Set<string>>(new Set());
  const busy = useRef(false);
  const finished = useRef(false);
  const catalogs = useRef<Catalogs | null>(null);
  const [tick, setTick] = useState(0);
  // Keep callbacks fresh without re-triggering the effect on every parent render.
  const cbRef = useRef({ onProgress, onDone });
  cbRef.current = { onProgress, onDone };

  useEffect(() => {
    if (!ready || !client || busy.current) return;
    const next = jobs.find((j) => !done.current.has(j._id));
    if (!next) {
      if (!finished.current) {
        finished.current = true;
        cbRef.current.onDone?.();
      }
      return;
    }
    busy.current = true;
    done.current.add(next._id);

    (async () => {
      let modelId: string | null = null;
      try {
        if (!catalogs.current) catalogs.current = await client.ring.getCatalogs();
        const finishId = catalogs.current.metalFinishes[0]?.id ?? "";

        const { url } = await getModelGlbUrl(next.fileId);
        // Read first: whether the model has a saved scene decides what happens
        // to a scene inside its file (v65 — the saved one wins). A model that
        // cannot be read renders on the batch settings, as before.
        const model = await getModel(next.fileId).catch(() => null);
        const vjson = (model?.config as { vjson?: string } | undefined)?.vjson;
        modelId = await client.ring.addModelUrl(
          url,
          withEmbeddedScene({ autoCenter: true, autoScale: true }, capsFor(client.contractVersion).sceneFileV65, !!vjson),
        );

        /*
         * Replay the file's OWN saved scene, exactly as /studio does on open
         * (StudioPage: model.config.vjson -> loadVjsonFile). This runner mounts
         * its own hidden ViewerHost, so without it every bulk render shot the
         * boot defaults — which is the literal second half of "picture comes
         * with grey background only, REGARDLESS OF THEME I CHOOSE". The theme
         * was being chosen in the studio and then discarded here.
         *
         * Best-effort: a file with no saved scene, or a scene that fails to
         * parse, still renders on the batch settings rather than failing the job.
         */
        try {
          if (vjson) {
            await client.ring.loadVjsonFile(
              new File([vjson], "config.vjson", { type: "application/json" }),
            );
          }
        } catch {
          /* no saved scene, or it would not load — fall through to batch settings */
        }

        await new Promise((r) => setTimeout(r, 2500)); // let it settle + render

        /*
         * "Use each file's effective settings" is the default and is sold as the
         * safest option, but nothing ever read it — the flag was stored on the
         * batch and never consulted, so per-model resolution/background set in
         * Render settings was silently ignored by every bulk render.
         *
         * `cameraAngles` is deliberately NOT applied: the settings UI names them
         * ("Perspective 3/4", "Macro (stone)") with no defined mapping onto the
         * runner's CameraPresets, and guessing would be worse than the batch-level
         * angle count the user already chose.
         */
        let shotW = width;
        let shotH = height;
        // A transparent-background file cannot be shot as JPEG: JPEG has no alpha,
        // so the browser flattens the alpha-0 canvas onto BLACK. The setting was
        // being honoured on the scene and thrown away at the encoder.
        let shotTransparent = false;
        if (useEffective) {
          try {
            const eff = await getEffectiveSettings(next.fileId);
            const px = RESOLUTION_PX[String(eff.resolution)];
            if (px) [shotW, shotH] = px;
            if (eff.transparentBg === true) { await client.ring.setTransparentBackground(); shotTransparent = true; }
            else if (typeof eff.background === "string" && eff.background) {
              await client.ring.setBackground(eff.background);
            }
            await new Promise((r) => setTimeout(r, 300));
          } catch {
            /* per-file settings are best-effort — fall back to the batch values */
          }
        }

        // metals<=1 → single pass on the model's current material (metal="").
        const metalIds =
          metals <= 1 ? [""] : catalogs.current.metalPresets.slice(0, metals).map((p) => p.id);
        const anglePresets = ANGLE_PRESETS.slice(0, Math.max(1, angles));
        const totalShots = metalIds.length * anglePresets.length;

        let shot = 0;
        for (const metal of metalIds) {
          if (metal) {
            const groups = await client.ring.listGroups();
            for (const g of groups) {
              if (g.kind === "metal") await client.ring.applyMetalPresetToGroup(g.id, metal, finishId);
            }
            await new Promise((r) => setTimeout(r, 400)); // let the new material settle
          }
          for (const angle of anglePresets) {
            await client.ring.setCameraPreset(angle);
            await new Promise((r) => setTimeout(r, 500));
            // JPEG (opaque catalogue render) — a 4K PNG base64 blows past the API
            // body limit; JPEG is 5-10× smaller and fits comfortably.
            // snapshotTiled handles >4K stills (exceeds the GPU max-renderbuffer size).
            // A transparent file is the exception: it MUST be PNG or the alpha is
            // flattened onto black. Cut-outs are near-flat, so they still fit the
            // 40 MB body limit comfortably.
            const fmt = shotTransparent ? "png" : "jpeg";
            const opts = { width: shotW, height: shotH, format: fmt as "png" | "jpeg", transparent: shotTransparent };
            const blob =
              shotW > 4096 || shotH > 4096
                ? await client.ring.snapshotTiled(opts)
                : await client.ring.snapshot(shotTransparent ? opts : { ...opts, quality: 0.92 });
            const dataUrl = await blobToDataURL(blob);
            await addJobOutput(next._id, { dataUrl, angle, metal });
            shot++;
            await updateJob(next._id, { progress: Math.round((shot / totalShots) * 90) });
          }
        }

        // A turntable video per model (in addition to the stills). Capped at 1080p —
        // a 4K video base64 would blow the API body limit; a 4s clip is a few MB.
        if (video) {
          await client.ring.setCameraPreset("threeQuarter");
          await new Promise((r) => setTimeout(r, 300));
          const vblob = await client.ring.exportTurntableVideo({
            width: Math.min(width, 1920),
            height: Math.min(height, 1080),
            fps: 30,
            durationMs: 4000,
            revolutions: 1,
            format: "webm",
          });
          await addJobOutput(next._id, { dataUrl: await blobToDataURL(vblob), angle: "turntable", metal: "" });
        }
        await updateJob(next._id, { progress: 100 });
        await updateJob(next._id, { done: true });
      } catch (e) {
        try {
          await updateJob(next._id, { error: e instanceof Error ? e.message : "Render failed" });
        } catch {
          /* ignore — job stays un-finished, batch poll still reflects the rest */
        }
      } finally {
        if (modelId) {
          try {
            client.ring.removeModel(modelId);
          } catch {
            /* ignore */
          }
        }
        busy.current = false;
        cbRef.current.onProgress?.();
        setTick((t) => t + 1); // pick the next job
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, client, jobs, tick]);

  return <>{iframe}</>;
}
